import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../../../lib/supabase';
import type { Project, ProjectInput, ProjectUpdate } from '../types';

interface UseProjectsReturn {
  projects: Project[];
  loading: boolean;
  addProject: (input: ProjectInput) => Promise<Project | null>;
  updateProject: (id: string, updates: ProjectUpdate) => Promise<void>;
  deleteProject: (id: string) => Promise<void>;
  refetch: () => Promise<void>;
}

export function useProjects(activeOrgId: string | null = null): UseProjectsReturn {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);

  const loadProjects = useCallback(async () => {
    setLoading(true);
    try {
      let query = supabase
        .from('projects')
        .select('*')
        .order('year', { ascending: false })
        .order('name');
      if (activeOrgId) query = query.eq('organization_id', activeOrgId);
      const { data } = await query;
      setProjects(data ?? []);
    } catch {
      setProjects([]);
    } finally {
      setLoading(false);
    }
  }, [activeOrgId]);

  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  useEffect(() => {
    window.addEventListener('planly:projects-changed', loadProjects);
    return () => window.removeEventListener('planly:projects-changed', loadProjects);
  }, [loadProjects]);

  const addProject = async ({ name, year }: ProjectInput): Promise<Project | null> => {
    let organizationId = activeOrgId;

    if (!organizationId) {
      const { data: authData } = await supabase.auth.getUser();
      if (!authData.user) return null;

      const { data: member } = await supabase
        .from('organization_members')
        .select('organization_id')
        .eq('user_id', authData.user.id)
        .maybeSingle();
      organizationId = member?.organization_id ?? null;
    }

    if (!organizationId) return null;

    const { data, error } = await supabase
      .from('projects')
      .insert({ name, year, organization_id: organizationId })
      .select()
      .single();
    if (error) throw error;
    await loadProjects();
    return data;
  };

  const updateProject = async (id: string, updates: ProjectUpdate): Promise<void> => {
    const { error } = await supabase.from('projects').update(updates).eq('id', id);
    if (error) throw error;
    await loadProjects();
  };

  const deleteProject = async (id: string): Promise<void> => {
    const { error } = await supabase.from('projects').delete().eq('id', id);
    if (error) throw error;
    await loadProjects();
  };

  return { projects, loading, addProject, updateProject, deleteProject, refetch: loadProjects };
}
